import { Units } from "@iventis/domain-model/model/units";
import { metersInKilometer, metersPerFoot, metersPerMile, metersSquaredInKiloMeterSquared, squareMetersPerSquareFoot, squareMetersPerSquareMile } from "./constants";

/** Whether a measurement stored in metres is a length (metres) or an area (square metres) */
export enum MeasurementKind {
    Area = "Area",
    Length = "Length",
}

const UNIT_SYMBOLS: Partial<Record<Units, string>> = {
    [Units.Meters]: "m",
    [Units.Kilometers]: "km",
    [Units.Feet]: "ft",
    [Units.Miles]: "mi",
};

const METERS_PER_UNIT: Partial<Record<Units, number>> = {
    [Units.Meters]: 1,
    [Units.Kilometers]: metersInKilometer,
    [Units.Feet]: metersPerFoot,
    [Units.Miles]: metersPerMile,
};

const SQUARE_METERS_PER_UNIT: Partial<Record<Units, number>> = {
    [Units.Meters]: 1,
    [Units.Kilometers]: metersSquaredInKiloMeterSquared,
    [Units.Feet]: squareMetersPerSquareFoot,
    [Units.Miles]: squareMetersPerSquareMile,
};

const METERS_PER_UNIT_BY_KIND: Record<MeasurementKind, Partial<Record<Units, number>>> = {
    [MeasurementKind.Area]: SQUARE_METERS_PER_UNIT,
    [MeasurementKind.Length]: METERS_PER_UNIT,
};

/**
 * Returns the abbreviated unit symbol, e.g. "m" or "ft²"
 * @param unit - The unit, null or unsupported units fall back to metres
 * @param kind - Whether the measurement is an area or a length
 */
export const getUnitSymbol = (unit: Units | null | undefined, kind: MeasurementKind): string => {
    const symbol = (unit == null ? undefined : UNIT_SYMBOLS[unit]) ?? UNIT_SYMBOLS[Units.Meters]!;
    return kind === MeasurementKind.Area ? `${symbol}²` : symbol;
};

/**
 * Converts a length in metres or an area in square metres to the given unit using exact conversion factors
 * @param value - Length in metres or area in square metres
 * @param unit - The unit to convert to, null or unsupported units leave the value in metres
 * @param kind - Whether the value is an area or a length
 */
export const convertFromMeters = (value: number, unit: Units | null | undefined, kind: MeasurementKind): number => {
    if (unit == null) {
        return value;
    }
    const metersPerUnit = METERS_PER_UNIT_BY_KIND[kind][unit];
    return metersPerUnit == null ? value : value / metersPerUnit;
};
